import type {
  Order,
  Product,
  ProductVariant,
} from "@shopify/hydrogen-react/storefront-api-types";
import TagManager from "@sooro-io/react-gtm-module";

import type { ICart } from "../queries/cart-gql";

export const APP_NAME = import.meta.env.VITE_REACT_APP_NAME as string;

export default function useTracking() {
  const gtmInit = () => {
    const lang = localStorage.getItem("i18nextLng");
    const GTM_KEY = {
      tune: "GTM-MKXPWZ2T",
      yeseyesee: lang === "ko" ? "GTM-TXM3GKTJ" : "GTM-PCGT6G5N",
      thisisneverthat:
        lang === "ko"
          ? "GTM-P7WWMKDB"
          : lang === "jp"
            ? "GTM-M3RCBLV8"
            : "GTM-W2XJNPD8",
      khakis: lang === "ko" ? "GTM-M4QMGQ6Q" : "GTM-PG22DBLF",
      sunlove: lang === "ko" ? "GTM-WN2L5TPH" : "GTM-W94RHRVV",
      wellbeingexpress: "GTM-PGZG8NMZ",
    };
    if (GTM_KEY[APP_NAME as keyof typeof GTM_KEY] !== "") {
      TagManager.initialize({
        gtmId: GTM_KEY[APP_NAME as keyof typeof GTM_KEY],
      });
    }
  };

  const gtmSelectItem = ({
    product,
    list,
    customerId,
  }: {
    product: Product;
    list: string;
    customerId?: string;
  }) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "select_item",
        user_id: customerId || undefined,
        ecommerce: {
          item_list_id: list,
          item_list_name: list,
          items: [
            {
              item_id: product.id.replace("gid://shopify/Product/", ""),
              item_name: product.title,
              item_brand: product.vendor,
              item_category: product.productType,
              price: product.priceRange.maxVariantPrice.amount,
              compare_at_price:
                product.compareAtPriceRange.maxVariantPrice.amount,
              available: product.availableForSale,
              image: product.featuredImage?.url,
              currency: product.priceRange.maxVariantPrice.currencyCode,
            },
          ],
        },
      },
    });
  };

  const gtmViewItem = (product: Product, customerId?: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "view_item",
        user_id: customerId || undefined,
        item_id: product.id.replace("gid://shopify/Product/", ""),
        item_name: product.title,
        item_brand: product.vendor,
        item_category: product.productType,
        price: product.priceRange.maxVariantPrice.amount,
        compare_at_price: product.compareAtPriceRange.maxVariantPrice.amount,
        available: product.availableForSale,
        image: product.featuredImage?.url,
        currency: product.priceRange.maxVariantPrice.currencyCode,
      },
    });
  };

  const gtmAddToCart = (
    variant: ProductVariant,
    customerId?: string,
    customerEmail?: string,
  ) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "add_to_cart",
        user_id: customerId || undefined,
        user_email: customerEmail || undefined,
        ecommerce: {
          items: [
            {
              item_variant_id: variant.id.replace(
                "gid://shopify/ProductVariant/",
                "",
              ),
              item_id: variant.sku,
              item_name: variant.product.title,
              item_brand: variant.product.vendor,
              item_variant_name: variant.title,
              item_category: variant.product.productType,
              quantity: 1,
              price: Number(variant.price.amount),
              compare_at_price: Number(variant.compareAtPrice?.amount),
              available: variant.availableForSale,
              image: variant.image?.url,
            },
          ],
          value: Number(variant.price.amount),
          currency: variant.price.currencyCode,
        },
      },
    });
  };

  const gtmAddToWishList = (variant: ProductVariant, customerId?: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "add_to_wishlist",
        user_id: customerId || undefined,
        ecommerce: {
          items: [
            {
              item_variant_id: variant.id.replace(
                "gid://shopify/ProductVariant/",
                "",
              ),
              item_name: variant.product.title,
              quantity: 1,
            },
          ],
          value: Number(variant.price.amount),
          currency: variant.price.currencyCode,
        },
      },
    });
  };

  const gtmPurchase = (
    order: Order,
    customerId?: string,
    customerEmail?: string,
  ) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "purchase",
        user_id: customerId,
        user_email: customerEmail || order.email,
        ecommerce: {
          order_id: order.name,
          order_total_amount: Number(order.totalPrice.amount),
          order_total_quantity: order.lineItems.edges.length,
          currency: order.totalPrice.currencyCode,
          order_items: order.lineItems.edges.map(({ node }, index) => {
            return {
              index,
              item_variant_id: node.variant?.id.replace(
                "gid://shopify/ProductVariant/",
                "",
              ),
              item_id: node.variant?.sku,
              item_name: node.variant?.product.title,
              item_brand: node.variant?.product.vendor,
              item_variant_name: node.variant?.title,
              item_category: node.variant?.product.productType,
              quantity: Number(node.quantity),
              price:
                Number(node.discountedTotalPrice.amount) /
                Number(node.quantity),
            };
          }),
        },
      },
    });
  };

  const gtmBeginCheckout = (
    cart: ICart["cart"],
    customerId?: string,
    customerEmail?: string,
  ) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "begin_checkout",
        user_id: customerId || undefined,
        user_email: customerEmail || undefined,
        ecommerce: {
          value: Number(cart.cost.totalAmount.amount),
          currency: cart.cost.totalAmount.currencyCode,
          items: cart.lines.edges.map(({ node }, index) => {
            const variant = node.merchandise;
            return {
              index,
              item_variant_id: variant.id.replace(
                "gid://shopify/ProductVariant/",
                "",
              ),
              item_id: variant.sku,
              item_name: variant.product.title,
              item_brand: variant.product.vendor,
              item_variant_name: variant.title,
              item_category: variant.product.productType,
              quantity: node.quantity,
              price: Number(variant.price.amount),
              compare_at_price: Number(variant.compareAtPrice?.amount),
              available: variant.availableForSale,
              image: variant.image?.url,
            };
          }),
        },
      },
    });
  };

  const gtmLocationChanged = (
    location: { pathname: string },
    customerId?: string,
  ) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "location_changed",
        user_id: customerId || undefined,
        page: location.pathname,
      },
    });
  };

  const gtmLogin = (
    method: "email" | "social",
    customerId?: string,
    customerEmail?: string,
  ): void => {
    TagManager.dataLayer({
      dataLayer: {
        event: "login",
        method,
        user_id: customerId || undefined,
        user_email: customerEmail || undefined,
      },
    });
  };

  return {
    gtmInit,
    gtmAddToCart,
    gtmAddToWishList,
    gtmViewItem,
    gtmSelectItem,
    gtmPurchase,
    gtmBeginCheckout,
    gtmLocationChanged,
    gtmLogin,
  };
}
